import {
	QueryClient,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { CompanyService } from '../company.service';

function mergeCompanyInCompaniesList(
	queryClient: QueryClient,
	company: Companies.Company,
) {
	queryClient.setQueriesData<Companies.GetCompaniesResponse>(
		{ queryKey: ['companies'] },
		(old) => {
			if (!old || Array.isArray(old)) return old;
			return {
				...old,
				data: (old.data ?? []).map((c) =>
					c.id === company.id
						? {
								...c,
								...company,
								_count: company._count ?? c._count,
						  }
						: c,
				),
			};
		},
	);
}

// --- Company Mutations ---

export const useCreateCompany = (
	onSuccess?: (company: Companies.Company) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Companies.Create) => CompanyService.create(body),
		onSuccess: (company) => {
			queryClient.setQueriesData<Companies.GetCompaniesResponse>(
				{ queryKey: ['companies'] },
				(old) => {
					if (!old || Array.isArray(old)) return old;

					const exists = old.data?.some((c) => c.id === company.id);
					if (exists) return old;

					const next = {
						...company,
						_count: company._count ?? {
							employees: 0,
							company_plan_subscription: 0,
						},
					};

					return {
						...old,
						data: [...(old.data ?? []), next].sort((a, b) =>
							(a.trade_name ?? '').localeCompare(
								b.trade_name ?? '',
								'pt-BR',
							),
						),
					};
				},
			);
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Empresa criada com sucesso!');
			if (onSuccess) onSuccess(company);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro na criação da empresa'));
		},
	});
};

export const usePatchCompany = (
	onSuccess?: (company: Companies.Company) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: Companies.Update }) =>
			CompanyService.patch(id, body),
		onSuccess: (company) => {
			mergeCompanyInCompaniesList(queryClient, company);
			queryClient.setQueryData<Companies.CompanyDetail>(
				['company', company.id],
				(old) => (old ? { ...old, ...company } : old),
			);
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			queryClient.invalidateQueries({
				queryKey: ['company', company.id],
			});
			queryClient.invalidateQueries({ queryKey: ['company-me'] });
			toast.success('Empresa atualizada com sucesso!');
			if (onSuccess) onSuccess(company);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização da empresa'),
			);
		},
	});
};

export const useRemoveCompany = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => CompanyService.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Empresa removida com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover empresa'));
		},
	});
};

export const useUpdateCompanyStatus = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, active }: { id: string; active: boolean }) =>
			CompanyService.updateStatus(id, { active }),
		onSuccess: (_data, { id, active }) => {
			queryClient.setQueriesData<Companies.GetCompaniesResponse>(
				{ queryKey: ['companies'] },
				(old) => {
					if (!old || Array.isArray(old)) return old;
					return {
						...old,
						data: (old.data ?? []).map((c) =>
							c.id === id ? { ...c, active } : c,
						),
					};
				},
			);
			queryClient.setQueryData<Companies.CompanyDetail>(
				['company', id],
				(old) => (old ? { ...old, active } : old),
			);
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			queryClient.invalidateQueries({ queryKey: ['company', id] });
			toast.success('Status da empresa atualizado!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar status'));
		},
	});
};

// --- Plan & Subscription Mutations ---

export const useCreatePlan = (onSuccess?: (plan: Companies.Plan) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Companies.CreatePlan) =>
			CompanyService.createPlan(body),
		onSuccess: (plan) => {
			queryClient.invalidateQueries({ queryKey: ['plans'] });
			toast.success('Plano criado com sucesso!');
			if (onSuccess) onSuccess(plan);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar plano'));
		},
	});
};

export const usePatchPlan = (onSuccess?: (plan: Companies.Plan) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: Companies.UpdatePlan;
		}) => CompanyService.updatePlan(id, body),
		onSuccess: (plan) => {
			queryClient.invalidateQueries({ queryKey: ['plans'] });
			toast.success('Plano atualizado com sucesso!');
			if (onSuccess) onSuccess(plan);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar plano'));
		},
	});
};

export const useRemovePlan = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => CompanyService.removePlan(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['plans'] });
			toast.success('Plano removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover plano'));
		},
	});
};

export const useCreateSubscription = (
	onSuccess?: (result: { url: string }) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			companyId,
			planId,
			successUrl,
			cancelUrl,
		}: {
			companyId: string;
			planId: string;
			successUrl: string;
			cancelUrl: string;
		}) =>
			CompanyService.createSubscription(
				companyId,
				planId,
				successUrl,
				cancelUrl,
			),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
			queryClient.invalidateQueries({ queryKey: ['company'] });
			toast.success('Checkout pronto para pagamento.');
			if (onSuccess) onSuccess(result);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar assinatura'));
		},
	});
};

export const useCancelSubscription = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: Companies.CancelSubscription;
		}) => CompanyService.cancelSubscription(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
			toast.success('Assinatura cancelada com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao cancelar assinatura'));
		},
	});
};

export const useLinkClub = (companyId: string, onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (clubId: string) =>
			CompanyService.linkClub(companyId, clubId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['company', companyId] });
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Clube vinculado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao vincular clube'));
		},
	});
};

export const useUnlinkClub = (companyId: string, onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (clubId: string) =>
			CompanyService.unlinkClub(companyId, clubId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['company', companyId] });
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast.success('Clube desvinculado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao desvincular clube'));
		},
	});
};
